/**
 * Pairing code shown as a QR and as copyable text.
 *
 * Format: `ENVR1-<CODE>` (app prefix + version + 5-digit room
 * code). The QR encodes exactly this string; the receiving page accepts
 * both the scanned QR and the pasted text. The transfer is encrypted E2E
 * by WebRTC's own DTLS, so the code does NOT carry a key (unlike
 * listto's data handoff, which added AES-GCM over the signaling);
 * the signaling server is ephemeral and single-use. Adding a room key
 * (to shield the handshake against a malicious relay) is a future improvement
 * documented in the guide.
 */

import { normalizeRoomCode, ROOM_CODE_LENGTH } from "./room-code";

export const TRANSFER_CODE_PREFIX = "ENVR1";

/** Builds the QR/copyable-code text from the room code. */
export function buildTransferCode(roomCode: string): string {
  return `${TRANSFER_CODE_PREFIX}-${normalizeRoomCode(roomCode)}`;
}

/**
 * Deep URL that the QR encodes: scanning it with the system camera (or
 * the app's) opens the receive page with the code already filled in and connects
 * on its own. `origin` is the sender's (same deployment for both peers).
 */
export function buildTransferUrl(origin: string, roomCode: string): string {
  const base = origin.replace(/\/+$/, "");
  return `${base}/receive?code=${buildTransferCode(roomCode)}`;
}

/**
 * Extracts the room code from a scanned/pasted text. Accepts the full
 * format `ENVR1-12345`, the bare code `12345` and the QR deep URL
 * (`https://…/receive?code=ENVR1-12345`). Returns `null` if there is no valid
 * room code.
 */
export function parseTransferCode(raw: string): string | null {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    let url: URL;
    try {
      url = new URL(trimmed);
    } catch {
      return null;
    }
    const param = url.searchParams.get("code");
    return param === null ? null : parseTransferCode(param);
  }
  const withoutPrefix = trimmed.toUpperCase().startsWith(`${TRANSFER_CODE_PREFIX}-`)
    ? trimmed.slice(TRANSFER_CODE_PREFIX.length + 1)
    : trimmed;
  const code = normalizeRoomCode(withoutPrefix);
  return code.length === ROOM_CODE_LENGTH ? code : null;
}
